import { Component, HostBinding, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';

import { PortalAuthService } from '../../portal-auth.service';
import { PortalBrandingService } from '../../portal-branding.service';
import { PortalProfileService } from '../../portal-profile.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { PortalOverlayService } from '../portal-overlay.service';

type HeaderMode = 'dashboard' | 'project' | 'section';

// The single top bar for the whole portal. `mode` selects the left/centre
// content — the dashboard's logo+name, the project hub's switch-project
// breadcrumb, or a section page's back button + section switcher — while the
// right-hand cluster (notifications + avatar → Edit Profile / Sign out) is
// always the same. The host element carries the per-mode container class so it
// replaces each screen's old header element 1:1 (styles live in styles.scss).
@Component({
	selector: 'portal-header',
	templateUrl: './portal-header.component.html',
	styleUrls: ['./portal-header.component.scss']
})
export class PortalHeaderComponent {

	@Input() mode: HeaderMode = 'dashboard';
	@Input() projectId = '';
	@Input() title = '';       // project name shown in the project breadcrumb
	@Input() current = '';     // current section key (section mode)
	@Input() sections: string[] = [];


	constructor(
		public auth: PortalAuthService,
		public branding: PortalBrandingService,
		public preview: PortalPreviewService,
		private profile: PortalProfileService,
		private router: Router,
		private overlay: PortalOverlayService
	) {}

	// Only one dropdown / panel may be open portal-wide.
	get menuOpen(): boolean { return this.overlay.isOpen(this); }

	@HostBinding('class.dash-header') get isDashboard() { return this.mode === 'dashboard'; }
	@HostBinding('class.detail-topbar') get isProject() { return this.mode === 'project'; }
	@HostBinding('class.sec-topbar') get isSection() { return this.mode === 'section'; }

	get avatarInitials(): string {
		const src = this.auth.user?.name || this.branding.companyName || '';
		const p = src.trim().split(/\s+/).filter(Boolean);
		return p.length ? (p[0][0] + (p[1]?.[0] || '')).toUpperCase() : '—';
	}

	@HostListener('document:click')
	closeMenu(): void { this.overlay.close(this); }

	toggleMenu(event: MouseEvent): void {
		event.stopPropagation();
		this.overlay.toggle(this);
	}

	openProfile(): void {
		this.overlay.close(this);
		this.profile.open();
	}

	// Project breadcrumb → project list; section back → the project hub.
	//
	// The state flag marks this as an explicit request to see the list: a customer
	// with a single project is otherwise forwarded straight back into it, which
	// would make this button look broken.
	goList(): void { this.router.navigate(['/'], { state: { switchProject: true } }); }
	goProject(): void { this.router.navigate(['/project', this.projectId]); }
}
